/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Eye, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Internal dependencies
 */
import ConfirmationDialog from '@Components/ConfirmationDialog';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { useCacheEntries, useDeleteCacheEntries } from '@Hooks/useCache';
import type { CacheEntrySummary } from '@Types/cache';
import { buildFilterParams } from '@Utils/buildFilterParams';
import CacheInspectDialog from './CacheInspectDialog';
import { formatBytes, formatTtl } from './utils';

const CACHE_FIELD_MAPPING: Record<string, string> = {
  tenant: 'tenant',
  restaurantGuid: 'restaurantGuid',
  resource: 'resource',
};

const cacheColumnConfigs: ColumnConfig<CacheEntrySummary>[] = [
  {
    id: 'tenant',
    headerLabel: 'Tenant',
    accessor: (row) => row.tenant,
    type: 'text',
    size: 140,
    cell: (info) => (
      <span className="font-mono text-xs">{info.getValue() as string}</span>
    ),
  },
  {
    id: 'restaurantGuid',
    headerLabel: 'Restaurant GUID',
    accessor: (row) => row.restaurantGuid,
    type: 'text',
    size: 280,
    cell: (info) => (
      <span className="font-mono text-xs">{info.getValue() as string}</span>
    ),
  },
  {
    id: 'resource',
    headerLabel: 'Resource',
    accessor: (row) => row.resource,
    type: 'text',
    size: 160,
    cell: (info) => (
      <Badge variant="outline">{info.getValue() as string}</Badge>
    ),
  },
  {
    id: 'bytes',
    headerLabel: 'Size',
    accessor: (row) => row.bytes,
    type: 'number',
    size: 100,
    cell: (info) => formatBytes(info.getValue() as number),
  },
  {
    id: 'ttlSeconds',
    headerLabel: 'TTL',
    accessor: (row) => row.ttlSeconds,
    type: 'number',
    size: 100,
    cell: (info) => formatTtl(info.getValue() as number),
  },
  {
    id: 'key',
    headerLabel: 'Key',
    accessor: (row) => row.key,
    type: 'text',
    size: 400,
    cell: (info) => (
      <span className="font-mono text-xs" title={info.getValue() as string}>
        {info.getValue() as string}
      </span>
    ),
  },
];

interface CacheListProps {
  renderHeaderActions?: () => React.ReactNode;
}

export default function CacheList({ renderHeaderActions }: CacheListProps) {
  const [inspectKey, setInspectKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CacheEntrySummary | null>(
    null,
  );

  const deleteMutation = useDeleteCacheEntries();

  const controller = useResourceTableController<CacheEntrySummary>({
    columnsConfig: cacheColumnConfigs,
    defaultSort: {
      id: 'bytes',
      desc: true,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: CACHE_FIELD_MAPPING,
      }),
    quickFilterFields: [
      { field: 'tenant', label: 'Tenant' },
      { field: 'restaurantGuid', label: 'Restaurant GUID' },
      { field: 'resource', label: 'Resource' },
    ],
    initialPageSize: 25,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setInspectKey(row.key);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteTarget(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: list,
    status,
    isError,
    error,
  } = useCacheEntries(controller.searchParams, {
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load cache entries',
      );
    }
  }, [isError, error]);

  const rows: CacheEntrySummary[] = list?.entries ?? [];
  const meta = list ? { total: list.total } : undefined;

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ key: deleteTarget.key });
      toastSuccess('Cache entry flushed.');
      setDeleteTarget(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to flush cache entry',
      );
    }
  };

  return (
    <>
      <ResourceTableView<CacheEntrySummary>
        label="Cache Entries"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryLabel="Total Entries: "
        renderExtraActions={renderHeaderActions}
      />

      <CacheInspectDialog
        cacheKey={inspectKey}
        onOpenChange={(open) => !open && setInspectKey(null)}
      />

      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onSubmit={handleDeleteConfirm}
        match="flush"
        title="Flush cache entry"
        description={
          deleteTarget
            ? `Remove cache entry for ${deleteTarget.resource} (${deleteTarget.tenant}:${deleteTarget.restaurantGuid})?`
            : ''
        }
        matchLabel='Type "flush" to confirm'
        submitLabel="Flush"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
